import { NervaUserData } from 'hydra-data-types';
import React, { ReactElement, useEffect } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import { ActionType } from '../../../store/ActionTypes';
import { store, useAppStore } from '../../../store/Store';
import { Analytics } from '../../../util/Analytics';
import { isReferred } from '../../../util/Cohorts';
import { ScreenWrapper } from '../../styles/Containers';
import { NavigationProps } from '../FlowNavigation';
import { PresentationLayout } from '../layouts/PresentationLayout';
import { getPapContinueActions, getPapResult } from './PapResult';

export type PapDiscountResultLayoutConfig = Record<string, never>;

export type PapDiscountResultScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.PapDiscountResult,
  PapDiscountResultLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  PapDiscountResultScreenConfig<ScreenNames>,
  ScreenNames
>;

function getPapAnalyticsVariant(isDirect: boolean) {
  return isDirect ? 'subscriptionNoInsurance' : 'referred';
}

export const PapDiscountResultScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const userData = useAppStore((state) => state.userData);
  const isDirect = !useAppStore(isReferred);
  const result = getPapResult(userData as NervaUserData, isDirect);
  const { score, tierConfig: tierMessage } = result;

  useEffect(function lockPapFlowAndTrackCompletion() {
    // Clear the page stack so the back button cannot navigate to previous PAP screens
    store.dispatch({
      type: ActionType.SET_PAGE,
      page: props.screenName,
      resetHistory: true,
    });

    Analytics.track({
      event: Analytics.Event.o_pap_completed,
      score,
      discount: tierMessage.discount,
      variant: getPapAnalyticsVariant(isDirect),
    });
  }, []);

  function onContinue() {
    getPapContinueActions(result).forEach((action) => store.dispatch(action));
    props.navigation.next(props, { resetHistory: true });
  }

  return (
    <ScreenWrapper>
      <PresentationLayout
        content={tierMessage.content.map((text) => ({ type: 'text' as const, content: text }))}
        primaryButtonTitle={tierMessage.buttonTitle}
        onPressPrimaryButton={onContinue}
      />
    </ScreenWrapper>
  );
};
